import { Component } from "@kluntje/core";
import { addClass, onEvent, removeClass, removeEvent, waitFor } from "@kluntje/js-utils/lib/dom-helpers";
import { LazyImage } from "Components/sde-e-lazy-image/sde-e-lazy-image";
import { OPEN } from "Constants/cssClasses";
import { GENERIC_CLOSED_EVENT } from "Constants/eventTypes";
import { render } from "lit-html";
import FocusService from "Services/FocusService";
import ScrollLockService from "Services/ScrollLockService";

export interface LightboxArgs {
  images: Array<LazyImage>;
  activeImageIdx: number;
}

export class SDELightbox extends Component {
  private _activeImageIdx = 0;
  private _images: Array<LazyImage>;

  private get activeImageIdx() {
    return this._activeImageIdx;
  }

  private set activeImageIdx(newValue: number) {
    if (newValue === this.activeImageIdx || newValue >= this.images.length || newValue < 0) return;
    this._activeImageIdx = newValue;
    this.renderLightbox();
  }

  private get images() {
    return this._images;
  }

  private set images(newValue: Array<LazyImage>) {
    if (newValue === this.images) return;
    this._images = newValue;
    this.renderLightbox();
  }

  public async openLightbox(args: LightboxArgs) {
    this.images = args.images;
    this.activeImageIdx = args.activeImageIdx;
    await waitFor(100);
    addClass(this, OPEN);
    await waitFor(300);
    onEvent(window, "keyup", this.handleKeyUp, this);
    FocusService.trapFocusIn(this);
    ScrollLockService.lockPage();
  }

  private async renderLightbox() {
    if (this.images === undefined) return;
    const { lightboxTemplate } = await import("./sde-m-lightbox.template");
    render(
      lightboxTemplate({
        activeImageIdx: this.activeImageIdx,
        images: this.images,
        component: this,
      }),
      this.getUiRoot(),
    );
  }

  private handleKeyUp(e: KeyboardEvent) {
    if (e.key === "Escape") {
      this.close();
      return;
    }

    if (e.key === "ArrowLeft") {
      this.handlePrevClick();
      return;
    }

    if (e.key === "ArrowRight") {
      this.handleNextClick();
      return;
    }
  }

  public handlePrevClick() {
    this.activeImageIdx--;
  }

  public handleNextClick() {
    this.activeImageIdx++;
  }

  public async close() {
    removeClass(this, OPEN);
    await waitFor(300);
    FocusService.unTrapFocusIn(this);
    ScrollLockService.unlockPage();
    removeEvent(window, "keyup", this.handleKeyUp, this);
    this.dispatchEvent(new CustomEvent(GENERIC_CLOSED_EVENT, { bubbles: false }));
    this.remove();
  }
}

customElements.define("sde-m-lightbox", SDELightbox);
